import { useNavigate, useSearchParams } from "react-router-dom";
import { Users } from "lucide-react";
import { EmptyState, Input, Page, Table, type Column } from "@marekl/adminer/components";
import { useDebouncedSearchFilter, useOptionalFilters } from "@marekl/adminer/hooks";
import { useSetTopbarTitle } from "@marekl/adminer/shell";
import { useCurrentCompany } from "../../lib/company";
import { useContactList, type Contact } from "../../lib/queries/contacts";
import { usePagedList } from "../../lib/usePagedList";
import { ListCard } from "../../components/ListCard";

export function addressLine(bd: Contact["billing_details"]): string {
  return [bd.street, [bd.post_code, bd.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

/** The address book (legacy "Adresář"): name, address, IČO, DIČ. */
export function ContactsPage() {
  const { id, can } = useCurrentCompany();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  useSetTopbarTitle("Adresář");
  const list = usePagedList(useContactList(id), { q: q || undefined }, {
    searchParams,
    setSearchParams,
    nounOf: ["kontaktu", "kontaktů"],
  });
  const { draft, setDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => list.filterReset((p) => (next ? p.set("q", next) : p.delete("q"))),
  });

  const columns: Column<Contact>[] = [
    {
      key: "name",
      header: "Obchodní název",
      primary: true,
      render: (c) => c.billing_details.business_name,
      linkTo: (c) => `/c/${id}/contacts/${c.id}`,
    },
    { key: "address", header: "Adresa", render: (c) => addressLine(c.billing_details) || "—" },
    { key: "ico", header: "IČO", render: (c) => c.billing_details.ico || "" },
    { key: "dic", header: "DIČ", render: (c) => c.billing_details.dic || "" },
  ];

  const filters = useOptionalFilters({
    filters: [
      {
        key: "q",
        label: "Název, IČO, DIČ nebo e-mail",
        alwaysOn: true,
        wide: true,
        active: !!q,
        onClear: () => list.filterReset((p) => p.delete("q")),
        render: () => <Input label="Název, IČO, DIČ nebo e-mail" value={draft} onChange={(e) => setDraft(e.target.value)} />,
      },
    ],
  });

  return (
    <Page>
      <Page.Content>
        <ListCard
          title="Seznam kontaktů"
          onCreate={can("editor") ? () => navigate(`/c/${id}/contacts/new`) : undefined}
          createLabel="nový"
          filters={filters}
          pagination={list.pagination}
          summary={list.summary}
        >
          <Table<Contact>
            cards
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey={(c) => c.id}
            isLoading={list.isLoading}
            error={list.error}
            onRetry={list.refetch}
            empty={<EmptyState icon={Users} title={q ? "Nic nenalezeno" : "Adresář je prázdný"} />}
          />
        </ListCard>
      </Page.Content>
    </Page>
  );
}
